import videojs from 'video.js'
import { registerPluginOnce } from '../utils.js'

import NFBCaptions from '../components/NFBCaption.js'
import NFBPlayback from '../components/NFBPlayback.js'

const Plugin = videojs.getPlugin('plugin')

const defaults = {
  theme: {
    name: 'default'
  },
  persistOptions: {
    persistVolume: true,
    persistCC: true,
    defaultCC: 'off'
  }
}

class NFBSettingsPanel extends Plugin {
  /**
   * Create a NFBSettingsPanel plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor(player, options) {
    super(player)
    this.log.level(videojs.log.level())
    this.options = videojs.obj.merge(defaults, options)
    const playerState = this.player.readyState()

    if (!playerState || playerState < 2) {
      this.player.one('loadeddata', this.init)
    } else {
      this.init()
    }

    this.player.ready(() => {
      this.player.on('userinactive', () => {
        this.closeSettingsPanel()
      })

      // this event ensures there is enough data loaded to play the media. It is triggered everytime a new source is loaded (initialization, on sourcechange, etc)
      this.player.on('canplay', () => {
        // before we create the playback component we check if it already exists and if there is any playback data present
        if (!this.player.el().querySelector('[data-ui-el="settings-panel-playback"]') && this.player.playbackRates().length > 0) {
          this.player.controlBar.addChild('NFBPlayback')
        }

        // before we create the captions component we check if it already exists
        if (!this.player.el().querySelector('[data-ui-el="settings-panel-caption"]')) {
          // in mobile, the player might indicate a false positive that a texttrack exists. This is why we check if there is at least one that has a language
          // before creating the component.
          if (this.player.textTracks().tracks_.length > 0 && this.player.textTracks().tracks_.some(el => el.language !== '')) {
            this.player.controlBar.addChild('NFBCaption')
          }
        }
      })
    })
  }

  init = () => {
    this.createSettingsPanel()
    this.addSettingsPanelButton()

    if (this.settingsButton) {
      this.settingsButton.on(['click', 'tap'], () => {
        this.handleSettingsButton()
      })
    }

    window?.addEventListener('orientationchange', () => {
      this.closeSettingsPanel()
    })

    this.settingsPanel?.addEventListener('focusout', (e) => {
      if (!this.settingsPanel.contains(e.relatedTarget) && e.relatedTarget !== this.settingsButton.el()) {
        this.closeSettingsPanel()
      }
    })
  }

  createSettingsPanel() {
    this.player.el().setAttribute('data-settings-panel-active', 'false')
    this.settingsPanel = this.player.controlBar.el().insertBefore(document.createElement('dialog'), this.player.el().querySelector('.vjs-fullscreen-control'))
    this.settingsPanel.classList.add('nfb-settings-panel__modal')
    this.settingsPanel.setAttribute('tabindex', '0')

    this.settingsInnerContainer = document.createElement('div')
    this.settingsInnerContainer.classList.add('nfb-settings-panel__container')
    this.settingsInnerContainer.setAttribute('data-ui-el', 'settings-panel')
    this.settingsInnerContainer.setAttribute('data-cy', 'settings-panel')

    this.settingsPanel.appendChild(this.settingsInnerContainer)

    // we trigger this event to let NFBHLSQualitySelector and NFBQualitySelector that the panel is ready to be injected to it.
    this.player.trigger('settingspanelready')
  }

  addSettingsPanelButton() {
    this.settingsButton = this.player.controlBar.addChild('button')
    this.settingsButton.addClass('nfb-settings-panel__button')
    this.settingsButton.setAttribute('data-ui-el', 'settings-panel-button')
    this.settingsButton.setAttribute('data-cy', 'settings-panel-button')
    if (this.options.theme.name === 'balmoral-neue') {
      this.settingsButton.addClass('nfb-button', 'nfb-button--round-with-icon', 'nfb-button--discreet', '_nfb-button--is-dark')
    }
    this.settingsButton.setAttribute('data-is-active', 'false')
    this.settingsButton.controlText(this.player.localize('Settings panel'))
    this.player.controlBar.el().insertBefore(this.settingsButton.el(), this.settingsPanel)
  }

  // due to the absolute positioning of the panel, and different orientation, we dynamically check and assign its height upon opening, relative to the player's height.
  openSettingsPanel() {
    const playerHeight = this.player.el().offsetHeight
    let settingsHeight = playerHeight * 0.7
    if (window.getComputedStyle(this.settingsInnerContainer).flexDirection === 'column') {
      this.settingsPanel.style.maxHeight = `${settingsHeight}px`
    } else {
      settingsHeight = playerHeight * 0.5
      this.settingsPanel.style.maxHeight = `${settingsHeight}px`
    }
    this.settingsPanel.show()
    this.settingsPanel.focus()
    this.settingsButton.setAttribute('data-is-active', 'true')
    this.player.el().setAttribute('data-settings-panel-active', 'true')
  }

  closeSettingsPanel() {
    this.settingsPanel?.close()
    this.settingsButton?.setAttribute('data-is-active', 'false')
    this.player.el().setAttribute('data-settings-panel-active', 'false')
  }

  handleSettingsButton() {
    if (this.settingsButton.el().dataset?.isActive === 'false') {
      this.openSettingsPanel()
    } else {
      this.closeSettingsPanel()
    }
  }
}

// Define default values for the plugin's `state` object here.
NFBSettingsPanel.defaultState = {}

// Include the version number.
NFBSettingsPanel.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbSettingsPanel', NFBSettingsPanel)

export default NFBSettingsPanel
